/**
 * CSV Import Service
 *
 * API service functions for CSV import wizard operations.
 * Uses apiPost/apiGet from @/lib/http/client for CSRF token handling.
 */

import { apiCall, apiGet, apiPost } from '@/lib/http/client';

// ============================================================================
// Types
// ============================================================================

export type CsvImportStatus =
  | 'analyzing'
  | 'ready_for_review'
  | 'importing'
  | 'completed'
  | 'failed';

export interface UploadedFileEntry {
  file_id: string | number;
  filename: string;
  entity_type_name: string;
  source_file_id?: number;
  new_properties?: NewPropertySuggestion[];
  /**
   * Set only on a source-driven import (e.g. Lightfield), which has no real
   * upload: it fabricates one virtual entry per entity type, each pointing at a
   * byte-less anchor file, purely to reuse the CSV pipeline. Its presence is
   * what tells the UI there is no file here — see `import-vocabulary.ts`.
   */
  source_import_anchor_file_id?: number;
  /** Persisted per source file so the "Internal columns" section survives a
   *  resume: CSV columns matching a system-managed property that can't be
   *  imported (e.g. ahoy_last_activity_at). */
  internal_columns?: string[];
}

export interface ColumnMapping {
  [csvColumn: string]: string; // csvColumn -> propertyName
}

export interface ColumnMappings {
  [fileId: string]: ColumnMapping; // fileId -> column mappings
}

export interface AssociationMapping {
  source_file_id: number | string;
  source_column: string;
  target_file_id: number | string;
  target_column: string;
  /**
   * Optional association type. When omitted, the backend infers the association
   * type for the (source, target) entity-type pair. Set explicitly to
   * disambiguate pairs that have more than one valid association type (the
   * user's choice in the mapping step).
   */
  association_type_name?: string;
}

/**
 * Maps a CSV column in a source file to an existing CRM entity looked up by
 * a target property value. Used for associating imported records with entities
 * already in the CRM (e.g. assigning an owner user to imported contacts).
 *
 * A **stage** mapping is the same shape used differently: `target_entity_type_name`
 * is a stage entity type (e.g. `ahoy_deal_pipeline_stage`), `target_property` is
 * unused (omitted/empty), and `value_mappings` carries the curated CSV value ->
 * stage entity UUID lookup instead of a property-lookup target.
 */
export interface CrmEntityAssociationMapping {
  source_file_id: number | string;
  source_column: string;
  target_entity_type_name: string;
  target_property?: string;
  /**
   * Optional association type. When omitted, the backend infers the single
   * association type for the (source, target) entity-type pair. Set explicitly
   * to disambiguate pairs that have more than one association type.
   */
  association_type_name?: string;
  /**
   * Stage mappings only: CSV value -> stage entity UUID (or `''` for the
   * deliberate "No stage" choice). Absent for non-stage association mappings.
   */
  value_mappings?: StageValueMapping;
}

export interface StageValueMapping {
  [csvValue: string]: string; // csvValue -> stage entity_id
}

export interface StageMappingEntry {
  column: string;
  value_mappings: StageValueMapping;
  /** Stage entity_id to apply when no value mapping matches. */
  default_stage?: string;
}

export interface StageMappings {
  [fileId: string]: StageMappingEntry;
}

export interface StageValueSuggestion {
  available_stages: string[];
  /** csvValue -> stage entity_id (or null when no confident match). */
  suggested_mappings: Record<string, string | null>;
  unique_values: string[];
  /** Stage names that matched more than one pipeline and were left unmapped. */
  ambiguous_stages?: string[];
}

/**
 * Pins a fixed association target on every created entity of a file's type —
 * the backing shape for the "Defaults & fixed values" panel (owner, note
 * visibility, ...). `target_kind: 'user'` resolves to that user's CRM entity
 * (owner / private-note visible-user); `target_kind: 'org'` resolves to the org
 * entity (public note visibility).
 */
export interface FixedValueMapping {
  source_file_id: number | string;
  association_type_name: string;
  target_kind: 'user' | 'org';
  target_user_id?: number;
}

export interface CsvImportCreateData {
  name: string;
  uploaded_files: UploadedFileEntry[];
  column_mappings: ColumnMappings;
  association_mappings?: AssociationMapping[];
  stage_mappings?: StageMappings;
  crm_entity_association_mappings?: CrmEntityAssociationMapping[];
  fixed_value_mappings?: FixedValueMapping[];
  match_columns?: Record<string, string[]>;
}

export interface AnalyzedRow {
  action: 'create' | 'update' | 'invalid' | 'skipped' | 'ambiguous';
  row_index: number;
  /** Mapped, validated data keyed by CRM property name (e.g. `ahoy_email`). */
  data: Record<string, unknown>;
  /**
   * Original CSV row keyed by the CSV column header (e.g. `Email`). The
   * backend uses this for cross-file association joins because the join
   * column is often a CSV column that isn't mapped to a CRM property
   * (e.g. a foreign-key column like "Associated Company"). Mirrors the
   * Python `analyzer._categorize_row` output.
   */
  raw_data?: Record<string, unknown>;
  entity_id?: string;
  existing_entity_id?: string;
  /**
   * True when this row was collapsed into an earlier row of the same
   * import because they share the same name and no unique-value property
   * (e.g. domain, email) was mapped. `action` stays `"update"` so the
   * executor still writes the row's properties onto the collapsed entity,
   * but the review UI renders these under a dedicated "Duplicates merged
   * within file" heading — they are NOT updates to an existing CRM record.
   */
  batch_deduplicated?: boolean;
  errors?: Array<{ property_name: string; error: string }>;
  /**
   * Set only by the `/rows` endpoint's per-row error cap (never by the inline
   * detail path, which always returns the full `errors` list uncapped).
   * `errors_truncated` is true when `errors` was cut to the cap; `error_count`
   * is the row's real (pre-cap) error count.
   */
  errors_truncated?: boolean;
  error_count?: number;
  /**
   * Agent-proposed corrected values for this invalid row's failing cells,
   * keyed by CRM property name. Populated by the `suggest-coercions` endpoint
   * (only proposals that pass real validation are stored).
   */
  proposals?: Record<string, { proposed_value: unknown; source: string }>;
  /**
   * The user's in-progress coercion edits, keyed by CRM property name. Saved by
   * `validate-coercions` on each change so the review can be resumed later.
   */
  coercion_edits?: Record<string, unknown>;
  conflicts?: Array<{
    property_name: string;
    csv_value: unknown;
    existing_value: unknown;
  }>;
  /**
   * Mirrors the backend's per-row stage resolution, consumed internally by the
   * executor's stage-association apply path. The review UI intentionally does
   * NOT read this field directly — change/conflict annotation is derived
   * solely from `conflicts`, never by inspecting `stage_data` in the browser.
   */
  stage_data?: {
    csv_value: string;
    stage_entity_id: string | null;
    valid: boolean;
    error?: string;
  };
  /** Number of existing CRM records that matched this row's match columns. Set by the analyzer only when action === 'ambiguous'. */
  match_count?: number;
  /** Resolved cross-file association targets to display for this row. Omitted when the row has no refs. */
  association_display?: Array<{
    label: string;
    value: string;
    resolved: boolean;
  }>;
}

/** Analyzer actions accepted by the /rows `action` filter (mirrors backend CSV_ROW_ACTION_FILTERS). */
export type CsvRowActionFilter =
  | 'create'
  | 'update'
  | 'invalid'
  | 'skipped'
  | 'ambiguous'
  | 'conflict';

export interface FileAnalysisResult {
  filename: string;
  entity_type_name: string;
  rows?: Record<string, unknown>[];
  row_count: number;
  analyzed_rows?: AnalyzedRow[];
  summary: {
    create_count: number;
    update_count: number;
    /** Rows collapsed into earlier rows of the same import — see AnalyzedRow.batch_deduplicated. */
    batch_dedup_count?: number;
    invalid_count: number;
    skipped_count?: number;
    stage_total?: number;
    stage_matched?: number;
    stage_unmatched?: number;
  };
}

export interface ConflictItem {
  key: string;
  file_id: string; // Backend returns string, not number
  row_index: number;
  property_name: string;
  csv_value: unknown;
  existing_value: unknown;
  /**
   * True when this conflict came from batch-deduplication (two rows in the
   * same CSV with the same name had different values for this property)
   * rather than from a CSV row conflicting with an existing CRM record.
   * The review UI uses this to swap the button labels from generic
   * "CSV Value" / "Existing Value" to row references.
   */
  is_batch_dedup?: boolean;
  /** 0-based row index in the source CSV that contributed csv_value. Set when is_batch_dedup. */
  csv_row_index?: number;
  /** 0-based row index in the source CSV that contributed existing_value. Set when is_batch_dedup. */
  existing_row_index?: number;
}

export interface FileSummary {
  filename: string;
  entity_type_name: string;
  total_rows: number;
  new_rows: number;
  update_rows: number;
  /** Rows collapsed into earlier rows of the same import. */
  batch_dedup_rows?: number;
  invalid_rows: number;
  /** @deprecated Use `invalid_rows`. Present in persisted analysis results from before the rename. */
  skip_rows?: number;
  conflict_rows: number;
  /** Rows the analyzer flagged ambiguous (multiple existing records matched) — not imported. */
  ambiguous_rows?: number;
  /** Set when this file's content (or name) matched a file from an earlier import
   *  in this org that may have written entities. Advisory — always rendered. */
  duplicate_import?: {
    match_type: 'content' | 'filename';
    prior_import_id: number;
    prior_import_name: string;
    /** ISO-8601 timestamp of the earlier import. */
    prior_import_date: string;
  };
  /** Set only alongside `duplicate_import`. True when this file's entity type has
   *  no mapped unique property and no resolvable match column, so approving would
   *  create duplicates. The backend rejects approval while this is true. */
  requires_match_columns?: boolean;
  /** Set only alongside `duplicate_import`. Reflects the `csv_imports.reimport_block_enabled`
   *  kill switch at analysis time — when false, an operator has turned the block off and
   *  the wizard's Approve button should not be disabled by `requires_match_columns`. */
  reimport_block_enabled?: boolean;
}

export interface AssociationsData {
  pending_associations: Array<{
    source_file_id: number | string;
    source_row_index: number;
    target_file_id: number | string;
    target_row_index: number;
  }>;
  unmatched_associations: UnmatchedAssociation[];
  /** Association mappings whose target property doesn't exist on the target
   *  entity type — a mapping-level (not row-level) problem, so every
   *  association using this mapping is skipped. OPTIONAL for back-compat
   *  with analysis results stored before this field was added. */
  invalid_associations?: InvalidCrmEntityAssociation[];
}

/** A CRM-entity association mapping whose `target_property` doesn't exist on
 *  `target_entity_type_name`. Distinct from `UnmatchedCrmEntityAssociation`
 *  (a per-row value that failed to match): this is a per-mapping problem —
 *  every row using this mapping will be skipped, so there's no per-row data
 *  to report. */
export interface InvalidCrmEntityAssociation {
  source_file_id: number | string;
  source_column: string;
  target_entity_type_name: string;
  target_property: string;
}

/** Where an unmatched association was trying to find its target. The two rails
 *  answer the same question against different things, so an entry carries one
 *  target shape or the other — never both. */
interface UnmatchedAssociationSource {
  source_file_id: number | string;
  source_row_index: number;
  source_column: string;
  source_value: string;
}

/** Cross-file rail: the value should have matched a column in another CSV.
 *  Emitted once per unmatched row. */
export interface UnmatchedFileAssociation extends UnmatchedAssociationSource {
  target_file_id: number | string;
  target_column: string;
  target_entity_type_name?: undefined;
}

/** CSV-to-existing-record rail: the value should have matched a property on a
 *  record already in the CRM (an owner by email, a company or stage by name).
 *  Emitted once per DISTINCT value, carrying the first row that used it. */
export interface UnmatchedCrmEntityAssociation extends UnmatchedAssociationSource {
  target_entity_type_name: string;
  target_property: string;
  target_file_id?: undefined;
}

export type UnmatchedAssociation =
  | UnmatchedFileAssociation
  | UnmatchedCrmEntityAssociation;

export interface AnalysisResult {
  file_summaries?: Record<string, FileSummary>;
  conflicts?: ConflictItem[];
  associations?: AssociationsData;
  [fileId: string]:
    | FileAnalysisResult
    | Record<string, FileSummary>
    | ConflictItem[]
    | AssociationsData
    | undefined;
}

const RESERVED_KEYS = ['file_summaries', 'conflicts', 'associations'] as const;

export function getFileResults(
  result: AnalysisResult | null
): Record<string, FileAnalysisResult> {
  if (!result) {
    return {};
  }
  const fileResults: Record<string, FileAnalysisResult> = {};
  for (const [key, value] of Object.entries(result)) {
    if (
      !RESERVED_KEYS.includes(key as (typeof RESERVED_KEYS)[number]) &&
      value &&
      typeof value === 'object' &&
      'row_count' in value
    ) {
      fileResults[key] = value as FileAnalysisResult;
    }
  }
  return fileResults;
}

export interface ExecutionResult {
  created: number;
  updated: number;
  skipped: number;
  downgraded_properties?: Array<{
    prop_name: string;
    entity_type_name: string;
    distinct_value_count: number;
  }>;
}

export interface Progress {
  processed: number;
  total: number;
  phase?: 'pending' | 'processing' | 'linking';
}

export interface CsvImport {
  id: number;
  name: string;
  status: CsvImportStatus;
  uploaded_files: UploadedFileEntry[];
  column_mappings: ColumnMappings;
  association_mappings: AssociationMapping[];
  crm_entity_association_mappings: CrmEntityAssociationMapping[];
  stage_mappings: StageMappings;
  fixed_value_mappings: FixedValueMapping[];
  match_columns?: Record<string, string[]>;
  analysis_result: AnalysisResult | null;
  conflict_resolutions: Record<string, 'csv' | 'existing'> | null;
  coercion_overrides: CoercionOverrides | null;
  execution_result: ExecutionResult | null;
  progress: Progress | null;
  entity_id: string | null;
  started_at: string | null;
  ecs_task_arn: string | null;
  last_heartbeat_at: string | null;
  dispatched_at: string | null;
  ecs_attempt_count: number;
  error_message: string | null;
  user: number;
  created_at: string;
  updated_at: string;
  /** Set by the backend detail serializer ONLY when the analyzed-row count exceeds the org's
   *  inline-rows threshold. When true, `analysis_result` file blocks omit inline `analyzed_rows`;
   *  the client fetches problem rows via `getCsvImportRows`. Absent below threshold. */
  analyzed_rows_omitted?: boolean;
  /** Total analyzed-row count, set alongside `analyzed_rows_omitted` when rows are omitted. */
  total_analyzed_rows?: number;
}

export interface CsvImportListItem {
  id: number;
  name: string;
  status: CsvImportStatus;
  created_at: string;
  execution_result: ExecutionResult | null;
}

export interface ConflictResolutions {
  [conflictKey: string]: 'csv' | 'existing';
}

// ============================================================================
// Paginated Response Type
// ============================================================================

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

// ============================================================================
// Service Functions
// ============================================================================

/**
 * Create a new CSV import
 *
 * @param orgId - Organization ID
 * @param data - Import creation data (name, files, mappings)
 * @returns Promise resolving to created CsvImport
 */
export async function createCsvImport(
  orgId: number,
  data: CsvImportCreateData
): Promise<CsvImport> {
  return apiPost<CsvImport>(`/api/orgs/${orgId}/csv-imports/`, data);
}

/**
 * Update mappings on an existing import and re-trigger analysis.
 *
 * The import must be in `ready_for_review` status. Accepts the same payload
 * as create — the backend updates the import in-place, resets to `analyzing`,
 * and re-queues the analysis task.
 *
 * @param orgId - Organization ID
 * @param importId - CSV import ID to reanalyze
 * @param data - Updated import data (files, mappings)
 * @returns Promise resolving to updated CsvImport (status will be 'analyzing')
 */
export async function reanalyzeCsvImport(
  orgId: number,
  importId: number,
  data: CsvImportCreateData
): Promise<CsvImport> {
  return apiPost<CsvImport>(
    `/api/orgs/${orgId}/csv-imports/${importId}/reanalyze/`,
    data
  );
}

/**
 * Get a single CSV import by ID
 *
 * @param orgId - Organization ID
 * @param importId - CSV import ID
 * @returns Promise resolving to CsvImport
 */
export async function getCsvImport(
  orgId: number,
  importId: number
): Promise<CsvImport> {
  return apiGet<CsvImport>(`/api/orgs/${orgId}/csv-imports/${importId}/`);
}

export interface CsvImportRowsParams {
  action?: CsvRowActionFilter;
  /** Restrict to a single file's rows. The /rows output omits file_id, so callers needing file
   *  scope MUST pass fileId and tag results themselves. */
  fileId?: string;
  limit?: number;
  offset?: number;
}

/**
 * Fetch a bounded, paginated page of analyzed rows for a large import.
 * GET /api/orgs/{orgId}/csv-imports/{importId}/rows/?action=&file_id=&limit=&offset=
 * Each result has the AnalyzedRow shape EXCEPT it does not carry `file_id`.
 */
export async function getCsvImportRows(
  orgId: number,
  importId: number,
  params: CsvImportRowsParams = {}
): Promise<PaginatedResponse<AnalyzedRow>> {
  const search = new URLSearchParams();
  if (params.action) {
    search.set('action', params.action);
  }
  if (params.fileId !== undefined) {
    search.set('file_id', params.fileId);
  }
  if (params.limit !== undefined) {
    search.set('limit', String(params.limit));
  }
  if (params.offset !== undefined) {
    search.set('offset', String(params.offset));
  }
  const qs = search.toString();
  return apiGet<PaginatedResponse<AnalyzedRow>>(
    `/api/orgs/${orgId}/csv-imports/${importId}/rows/${qs ? `?${qs}` : ''}`
  );
}

/**
 * List all CSV imports for an organization
 *
 * @param orgId - Organization ID
 * @returns Promise resolving to list of CsvImportListItem
 */
export async function listCsvImports(
  orgId: number
): Promise<CsvImportListItem[]> {
  const response = await apiGet<PaginatedResponse<CsvImportListItem>>(
    `/api/orgs/${orgId}/csv-imports/`
  );
  if (!response.results) {
    throw new Error('Invalid paginated response: missing results field');
  }
  return response.results;
}

/**
 * Approve a CSV import for execution (transition from ready_for_review to importing)
 *
 * @param orgId - Organization ID
 * @param importId - CSV import ID
 * @returns Promise resolving to updated CsvImport
 */
export async function approveCsvImport(
  orgId: number,
  importId: number
): Promise<CsvImport> {
  return apiPost<CsvImport>(
    `/api/orgs/${orgId}/csv-imports/${importId}/approve/`,
    {}
  );
}

/**
 * Submit conflict resolutions for a CSV import
 *
 * @param orgId - Organization ID
 * @param importId - CSV import ID
 * @param resolutions - Map of conflict keys to resolution choices ('csv' | 'existing')
 * @returns Promise resolving to updated CsvImport
 */
export async function resolveConflicts(
  orgId: number,
  importId: number,
  resolutions: ConflictResolutions
): Promise<CsvImport> {
  return apiPost<CsvImport>(
    `/api/orgs/${orgId}/csv-imports/${importId}/resolve_conflicts/`,
    { resolutions }
  );
}

// ============================================================================
// Agent Coercion (opt-in fixing of invalid cells)
// ============================================================================

/** One record's in-progress coercion choices, for read-only validation. */
export interface CoercionDecision {
  file_id: string;
  row_index: number;
  /** Proposed/edited values keyed by CRM property name. */
  values: Record<string, unknown>;
}

/** Per-record result of validating in-progress coercion choices. */
export interface CoercionValidationResult {
  file_id: string;
  row_index: number;
  row_valid: boolean;
  errors: Array<{ property_name: string; error: string }>;
}

/** Accepted coercion values to commit, keyed `<file_id>:<row_index>:<property>`. */
export interface CoercionOverrides {
  [cellKey: string]: unknown;
}

/**
 * Ask the agent to propose corrected values for the import's invalid cells.
 * Proposals are persisted onto the analyzed rows (`proposals`) and returned in
 * the refreshed import detail.
 */
export async function suggestCoercions(
  orgId: number,
  importId: number
): Promise<CsvImport> {
  return apiPost<CsvImport>(
    `/api/orgs/${orgId}/csv-imports/${importId}/suggest-coercions/`,
    {}
  );
}

/**
 * Validate the user's in-progress coercion choices. Returns, per record, whether it
 * would now be fully valid and any residual errors — used for on-the-fly feedback as
 * the user accepts/edits proposals. Side effect: persists the submitted values as a
 * resumable draft (`coercion_edits`) on each row, so leaving and reopening the review
 * restores the in-progress edits. It does NOT re-categorize rows — that happens only on
 * apply.
 */
export async function validateCoercions(
  orgId: number,
  importId: number,
  decisions: CoercionDecision[]
): Promise<CoercionValidationResult[]> {
  const response = await apiPost<{ results: CoercionValidationResult[] }>(
    `/api/orgs/${orgId}/csv-imports/${importId}/validate-coercions/`,
    { decisions }
  );
  return response.results;
}

/**
 * Commit accepted coercion values. The backend stores them and re-runs analysis,
 * so fixed rows re-categorize (create/update). The returned import is back in
 * `analyzing` status — poll until `ready_for_review`, same as reanalyze.
 */
export async function applyCoercions(
  orgId: number,
  importId: number,
  overrides: CoercionOverrides
): Promise<CsvImport> {
  return apiPost<CsvImport>(
    `/api/orgs/${orgId}/csv-imports/${importId}/apply-coercions/`,
    { overrides }
  );
}

// ============================================================================
// Mapping Suggestions
// ============================================================================

export interface AssociationSuggestion {
  column: string;
  target_entity_type_name: string;
  target_property: string;
}

export interface MappingSuggestion {
  entity_type_name: string;
  mappings: Record<string, string>;
  associations: AssociationSuggestion[];
  confidence: number;
  stage_column?: string | null;
  new_properties?: NewPropertySuggestion[];
  /** CSV columns whose header matches a system-managed property that can't be
   *  imported (e.g. the ahoy_last_activity_at rollup). Surfaced read-only. */
  internal_columns?: string[];
  /** CSV headers that look like record identifiers (e.g. "Record ID") AND are
   *  mapped to a real property, so they can be used as unique match columns. */
  suggested_match_columns?: string[];
}

export interface SelectOptionSuggestion {
  value: string;
  label: string;
}

export interface NewPropertySuggestion {
  column: string;
  property_name: string;
  label: string;
  field_type_name: string;
  entity_type_name: string;
  options?: SelectOptionSuggestion[];
}

export interface EntityTypeMappingSuggestion {
  entity_type_name: string;
  mappings: Record<string, string>;
  new_properties: NewPropertySuggestion[];
}

export interface MultiTypeMappingSuggestion {
  entity_types: EntityTypeMappingSuggestion[];
  associations: AssociationSuggestion[];
  confidence: number;
  is_multi_type: boolean;
  /** CSV columns whose header matches a system-managed property that can't be
   *  imported (e.g. the ahoy_last_activity_at rollup). Surfaced read-only in a
   *  dedicated "Internal columns" section, not the unassigned bucket. */
  internal_columns?: string[];
  /** CSV headers that look like record identifiers (e.g. "Record ID") AND are
   *  mapped to a real property, so they can be used as unique match columns. */
  suggested_match_columns?: string[];
}

/**
 * Get AI-suggested object type and column mappings for CSV columns.
 *
 * @param orgId - Organization ID
 * @param columns - List of CSV column header names
 * @param entityTypeName - Optional entity type name to constrain suggestions
 * @param sampleData - Optional sample rows for field-type inference on unmapped columns
 * @returns Promise resolving to mapping suggestions
 */
export async function suggestMappings(
  orgId: number,
  columns: string[],
  entityTypeName?: string,
  sampleData?: Record<string, string>[],
  signal?: AbortSignal
): Promise<MappingSuggestion> {
  return apiCall<MappingSuggestion>(
    `/api/orgs/${orgId}/csv-imports/suggest-mappings/`,
    'POST',
    {
      columns,
      ...(entityTypeName ? { entity_type_name: entityTypeName } : {}),
      ...(sampleData ? { sample_data: sampleData } : {}),
    },
    undefined,
    signal
  );
}

/**
 * Get suggested stage value mappings for a CSV column.
 *
 * @param orgId - Organization ID
 * @param fileId - Uploaded file ID (numeric for real files, string for virtual files)
 * @param columnName - CSV column name containing stage values
 * @param entityTypeName - Entity type name to look up available stages
 * @param pipelineHintStageId - The file's chosen default stage. When present,
 *   the backend scopes stage resolution to that stage's pipeline.
 * @returns Promise resolving to stage value suggestions
 */
export async function suggestStageValues(
  orgId: number,
  fileId: string | number,
  columnName: string,
  entityTypeName: string,
  pipelineHintStageId?: string
): Promise<StageValueSuggestion> {
  // API requires integer file_id. For virtual files (e.g. "42_ahoy_contact"),
  // extract the numeric source file ID prefix.
  let numericFileId: number;
  if (typeof fileId === 'string') {
    const parsed = parseInt(fileId, 10);
    if (isNaN(parsed)) {
      throw new Error(`Invalid virtual file ID: ${fileId}`);
    }
    numericFileId = parsed;
  } else {
    numericFileId = fileId;
  }
  return apiPost<StageValueSuggestion>(
    `/api/orgs/${orgId}/csv-imports/suggest-stage-values/`,
    {
      file_id: numericFileId,
      column_name: columnName,
      entity_type_name: entityTypeName,
      ...(pipelineHintStageId
        ? { pipeline_hint_stage_id: pipelineHintStageId }
        : {}),
    }
  );
}

/**
 * Get suggested select options by reading full column data from uploaded CSV file(s).
 *
 * The backend reads the actual files, extracts all values from the named column,
 * deduplicates case-insensitively, sorts alphabetically, and caps at MAX_SELECT_OPTIONS.
 *
 * @param orgId - Organization ID
 * @param fileIds - IDs of uploaded files to read column data from
 * @param columnName - CSV column header name to extract values from
 * @returns Promise resolving to deduplicated options and whether they were truncated
 */
export async function suggestSelectOptions(
  orgId: number,
  fileIds: number[],
  columnName: string
): Promise<{ options: SelectOptionSuggestion[]; truncated: boolean }> {
  return apiCall<{ options: SelectOptionSuggestion[]; truncated: boolean }>(
    `/api/orgs/${orgId}/csv-imports/suggest-select-options/`,
    'POST',
    { file_ids: fileIds, column_name: columnName }
  );
}

/**
 * Get AI-suggested multi-type mappings for CSV columns that may contain multiple entity types.
 *
 * @param orgId - Organization ID
 * @param columns - List of CSV column header names
 * @param sampleData - Sample rows from the CSV for analysis
 * @returns Promise resolving to multi-type mapping suggestions
 */
export async function suggestMultiTypeMappings(
  orgId: number,
  columns: string[],
  sampleData: Record<string, string>[],
  signal?: AbortSignal
): Promise<MultiTypeMappingSuggestion> {
  return apiCall<MultiTypeMappingSuggestion>(
    `/api/orgs/${orgId}/csv-imports/suggest-multi-type-mappings/`,
    'POST',
    { columns, sample_data: sampleData },
    undefined,
    signal
  );
}
